// Same-origin JSON API client: session cookie (HttpOnly, set by the server) + CSRF header. The CSRF token is
// taken from response bodies (/auth/csrf, /auth/me), never from cookies.
import { TransportError } from '@/app/ports';
import { current as APP_VERSION } from '../../../docs/release-notes.json';

let csrf: string | null = null;
const REQUEST_TIMEOUT_MS = 30_000;
export const setCsrf = (t: string | null) => { csrf = t; };
/** Client hint (ADR 0018): lets Profile › Devices name this session's app; the server falls back to the User-Agent. */
export const CLIENT_HINT = 'web/' + String(APP_VERSION || '0');

export interface ApiErrorBody { error?: { code?: string; message?: string; details?: Record<string, unknown> } }

export async function api<T>(method: string, path: string, body?: unknown, opts: { signal?: AbortSignal; retried?: boolean; raw?: { body: Blob; headers: Record<string, string> } } = {}): Promise<T> {
  // After an offline boot no session CSRF token is known yet: obtain it before the first write.
  if (method !== 'GET' && !csrf && !path.startsWith('/auth/')) await refreshSessionCsrf();
  let res: Response;
  // A request that never answers (weak or captive connection) counts as offline after a while, so sync does not
  // hang in "Syncing"; uploads (raw bodies) may take longer and keep only the caller's signal.
  const ctl = opts.raw ? null : new AbortController();
  const timer = ctl ? setTimeout(() => ctl.abort(), REQUEST_TIMEOUT_MS) : null;
  if (ctl && opts.signal) { if (opts.signal.aborted) ctl.abort(); else opts.signal.addEventListener('abort', () => ctl.abort(), { once: true }); }
  try {
    res = await fetch('/api/v1' + path, {
      method, credentials: 'same-origin', cache: 'no-store', signal: ctl ? ctl.signal : opts.signal,
      headers: { Accept: 'application/json', 'X-TaskCool-Client': CLIENT_HINT, ...(opts.raw ? { 'Content-Type': 'application/octet-stream', ...opts.raw.headers } : body !== undefined ? { 'Content-Type': 'application/json' } : {}), ...(method !== 'GET' && csrf ? { 'X-CSRF-Token': csrf } : {}) },
      body: opts.raw ? opts.raw.body : body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    throw new TransportError('network', (e as Error).message || 'network error');
  } finally {
    if (timer) clearTimeout(timer);
  }
  if (res.status === 204) return undefined as T;
  let data: unknown = null;
  try { data = await res.json(); } catch { /* non-JSON */ }
  if (res.ok) return data as T;
  const err = (data as ApiErrorBody)?.error || {};
  const code = err.code || '';
  if (code === 'csrf_failed' && method !== 'GET' && !opts.retried && !path.startsWith('/auth/')) { await refreshSessionCsrf(); return api<T>(method, path, body, { ...opts, retried: true }); }
  const kind = res.status === 401 ? 'auth' : res.status === 403 ? 'forbidden' : res.status === 409 ? 'conflict' : res.status === 410 ? 'cursor_expired' : res.status >= 500 || res.status === 0 ? 'server' : 'rejected';
  throw new TransportError(kind, err.message || res.statusText, res.status, code);
}

export async function ensureCsrf(): Promise<string> {
  if (csrf) return csrf;
  const r = await api<{ csrf_token: string }>('GET', '/auth/csrf');
  csrf = r.csrf_token;
  return csrf;
}

/** Session CSRF for authenticated `/auth/*` writes (api() only pre-fetches it for non-auth paths). */
export async function ensureSessionCsrf(force = false): Promise<void> { if (force || !csrf) await refreshSessionCsrf(); }

/** Re-reads the session's CSRF token from /auth/me (401 surfaces as an auth error to the caller). */
async function refreshSessionCsrf(): Promise<void> {
  const r = await api<{ csrf_token: string }>('GET', '/auth/me');
  csrf = r.csrf_token;
}
